<template>
    <div class="patient-share-video">
        <div class="video-container" @click="togglePlayerButtons">
            <video :id="playerId" :controls="false" class="video-tag" preload="auto" playsinline>
                <!-- timecode postfix is for thumbnail in iPhone & iPad -->
                <source :src="`${well.videoUrl}#t=0.001`" type="video/mp4" />
            </video>

            <template v-if="videoPlaybackReady">
                <well-status-overlay class="rounded-top-lg" :well="well" />

                <well-time-view :current-well-time_h="currentWellTime_h" />

                <player-navigation
                    v-if="showPlayerButtons"
                    :play-state="playState"
                    video-seek-mode="event"
                    @playToggled="setPlayState"
                    @skipClicked="setSelectedDay"
                    @click.native.stop="togglePlayerButtons"
                />
            </template>

            <local-loader v-if="!videoPlaybackReady" />
        </div>

        <standalone-video-seek-bar
            v-if="videoPlaybackReady"
            :well="well"
            :video-length_s="well.slide.videoLength"
            :current-time-code_s="currentTimeCode_s"
            :first-run-time_h="well.runs[0].time"
            :current-well-time_h="currentWellTime_h"
            :slide-incubation-length="slideIncubationLength"
            @seekVideoTo="seekVideoTo"
        />
    </div>
</template>

<script>
const HOURS_IN_DAY = 24

import Utils from '../../../business/utils/Utils'
import UIUtils from '../../../business/utils/UIUtils'

import LocalLoader from '../common/LocalLoader'
import WellTimeView from '../common/WellTimeView'
import WellStatusOverlay from '../common/WellStatusOverlay'
import PlayerNavigation from '../video/PlayerNavigation'
import StandaloneVideoSeekBar from '../common/StandaloneVideoSeekBar'

export default {
    name: 'PatientShareVideo',
    components: {
        LocalLoader,
        WellTimeView,
        WellStatusOverlay,
        PlayerNavigation,
        StandaloneVideoSeekBar
    },
    props: {
        well: {
            type: Object,
            required: true
        },
        selectedDay: {
            type: Number,
            required: true
        },
        latestDay: {
            type: Number,
            required: true
        }
    },
    data: () => ({
        videoPlaybackReady: false,
        player: null,
        currentTimeCode_s: 0,
        playState: 'pause',
        showPlayerButtons: true
    }),
    computed: {
        playerId() {
            return `shared_video_${this.well._id}`
        },
        fpsCalibrationTime_s() {
            if (!this.videoFPS) return 0

            return Math.round((1 / this.videoFPS / 2) * 1000000) / 1000000
        },
        videoFPS() {
            const { numberOfRuns, videoLength } = this.well.slide

            return Math.round((numberOfRuns + 1) / videoLength)
        },
        currentRunIndex() {
            const { numberOfRuns, videoLength } = this.well.slide

            return Utils.calculateRunIndex(this.currentTimeCode_s, numberOfRuns, videoLength)
        },
        currentRun() {
            return this.well.runs[this.currentRunIndex]
        },
        currentWellTime_h() {
            return this.currentRun ? this.currentRun.time : 0
        },
        slideIncubationLength() {
            return this.well.runs.last().time
        }
    },
    watch: {
        videoPlaybackReady(isReady) {
            if (isReady) {
                this.player.addEventListener('timeupdate', this.onTimeUpdate)

                UIUtils.hideLoadingMask()
            } else {
                UIUtils.showLoadingMask()
            }
        },
        playState(newState) {
            this.$log.videoEvent('play_stop')
            newState === 'play' ? this.player.play() : this.player.pause()
        },
        selectedDay(newVal) {
            this.skipToDay(newVal)
        }
    },
    mounted() {
        this.loadVideo()
    },
    methods: {
        loadVideo() {
            this.videoPlaybackReady = false
            this.player = document.getElementById(this.playerId)

            const self = this
            this.player.addEventListener('canplaythrough', function () {
                self.videoPlaybackReady = true
            })
        },
        onTimeUpdate() {
            const currentTime = this.player.currentTime

            let timecode = parseFloat(currentTime.toFixed(2))

            if (timecode > 0) {
                timecode -= this.fpsCalibrationTime_s
            }

            this.currentTimeCode_s = timecode
        },
        setPlayState(playState) {
            if (this.player) {
                this.playState = playState
            }
        },
        setSelectedDay(isForward) {
            const delta = isForward ? 1 : -1
            const targetDayCount = this.selectedDay + delta

            if (targetDayCount && targetDayCount <= this.latestDay) {
                this.$emit('selectDay', targetDayCount)
            }
        },
        seekVideoTo(timecode) {
            if (this.player && this.videoPlaybackReady) {
                timecode = parseFloat(timecode.toFixed(2))

                let additionalTimeFraction =
                    (Math.round(timecode * 1000000) % Math.round((1 / this.videoFPS) * 1000000)) /
                    1000000

                additionalTimeFraction = parseFloat(additionalTimeFraction.toFixed(4))

                if (additionalTimeFraction > 0) {
                    timecode = timecode - additionalTimeFraction
                }

                timecode += this.fpsCalibrationTime_s
                timecode = parseFloat(timecode.toFixed(2))

                this.player.currentTime = timecode
            }
        },
        findRunIndex(dayCount) {
            const startTime = (dayCount - 1) * HOURS_IN_DAY

            const firstRunThisDayIdx = this.well.runs.findIndex(
                run => !!run && run.time >= startTime
            )

            // if day is later than video length, take latest run
            if (firstRunThisDayIdx === -1) {
                return this.well.runs.length - 1
            }

            return firstRunThisDayIdx
        },
        skipToDay(day) {
            const runIdx = this.findRunIndex(day)

            this.seekVideoTo(runIdx / this.videoFPS)
        },
        togglePlayerButtons() {
            this.showPlayerButtons = !this.showPlayerButtons
        }
    }
}
</script>

<style lang="scss" scoped>
.patient-share-video {
    position: relative;
    margin: 10px 0;

    .video-container {
        // needed to keep stable shape while video loading
        width: 100%;
        aspect-ratio: 1;

        .video-tag {
            width: 100%;
            height: 100%;
        }

        .well-status-overlay {
            z-index: 2;
        }

        .well-time-view {
            position: absolute;
            right: 1rem;
            top: 1rem;
            padding: 5px 10px;
            border-radius: 4px;
            background-color: $dark-5;
        }

        .player-navigation {
            width: 100%;
            height: 100%;
            position: absolute;
            top: 0;
            left: 0;
            background-color: $dark-3-alpha;
            z-index: 3;
        }

        .local-loader {
            z-index: 5;
        }
    }
}
</style>

<style lang="scss">
.patient-share-video {
    .player-navigation {
        .backward,
        .forward,
        .play {
            i {
                font-size: 40px;
            }
        }
    }

    .standalone-video-seek-bar {
        margin: 0 0.5rem;
        width: calc(100% - 1rem);
        position: absolute;
        bottom: -10px;
        z-index: 10;

        .video-seek-bar {
            .slider {
                .slider-track {
                    height: 5px;
                    background-color: $dark-2;
                    border-radius: 3px;

                    .slider-selection {
                        background-color: $pink;
                    }
                }

                .slider-tick-container {
                    .slider-tick {
                        height: 5px;
                    }
                }

                .slider-handle {
                    width: 1rem;
                    height: 1rem;
                    margin-left: -0.5rem;
                    margin-top: 0;
                    border: none;
                    background: $pink;
                }
            }
        }
    }

    @media screen and #{$new-break3open} {
        border-radius: 6px;

        .video-tag {
            border-radius: 6px;
        }
    }
}
</style>
